import { ReactComponent as NXlogo } from "@/assets/icons/nexus-logo.svg";
import { Icon } from "@nexus/shared-ui";
import { Button, Flex, Table, Typography } from "antd";
import { useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useReactToPrint } from "react-to-print";

const { Text } = Typography;

const RecapPrint = ({ data }: { data?: any }) => {
  const { t } = useTranslation();
  const printRef = useRef<HTMLDivElement>(null);

  const documentTitle = useMemo(() => {
    const title = data?.meetTitle?.trim() || "Recap";
    const sessionLabel =
      typeof data?.selectedSessionLabel === "string"
        ? data.selectedSessionLabel
        : "";

    return `${title}${sessionLabel ? ` - ${sessionLabel}` : ""}`
      .replace(/[\\/:*?"<>|]/g, "-")
      .replace(/\s+/g, " ")
      .trim();
  }, [data?.meetTitle, data?.selectedSessionLabel]);

  const conductingStatus = useMemo<string>(
    () =>
      t("recap.statusDescription", {
        ...data?.participants?.stats,
        ...data?.recapOverview?.conductingStatusValues,
        delayTime:
          data?.recapOverview?.conductingStatusValues?.delayTime ||
          t("recap.none"),
      }) as string,
    [t, data]
  );

  const onPrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: () => documentTitle,
  });

  const actionItemColumns = useMemo(
    () => [
      {
        title: t("recap.title"),
        dataIndex: "title",
        key: "title",
        render: (_: string, record: any) =>
          record?.title || record?.description || "-",
      },
      {
        title: t("recap.assignee"),
        dataIndex: "assigneeName",
        key: "assigneeName",
        render: (_: string, record: any) =>
          record?.assigneeName ||
          record?.user?.fullName ||
          t("recap.unassigned"),
      },
    ],
    [t]
  );

  const decisionColumns = useMemo(
    () => [
      {
        title: t("recap.title"),
        dataIndex: "description",
        key: "description",
        render: (_: string, record: any) =>
          record?.description || record?.title || "-",
      },
    ],
    [t]
  );

  return (
    <>
      <Button
        type="default"
        icon={<Icon name="UilPrint" size={16} />}
        onClick={onPrint}
      />

      <div className="d-none">
        <div ref={printRef} className="recap-print-wrapper">
          <Flex vertical gap={36} className="p-9">
            <Flex align="center" justify="center">
              <NXlogo width={130} height={30} />
            </Flex>

            <Flex vertical align="center" gap={8}>
              <Text className="h3 font-weight-500">
                {data?.meetTitle?.trim() || "-"}
              </Text>

              <Flex justify="center" align="center" gap={8} wrap>
                {data?.selectedSessionLabel ? (
                  <Text>📅{data.selectedSessionLabel}</Text>
                ) : null}

                <Text>👤{t("recap.organizedBy")}</Text>

                <Text>{data?.participants?.stats?.organizer || "-"}</Text>

                <Text>📍{t("recap.online")}</Text>
              </Flex>
            </Flex>

            <Flex vertical gap={8}>
              <Text className="h5 font-weight-500">
                ● {t("recap.conductingStatus")}
              </Text>
              <Text>{conductingStatus}</Text>
            </Flex>

            {data?.summary?.trim() ? (
              <Flex vertical gap={8}>
                <Text className="h5 font-weight-500">
                  ● {t("recap.summary")}
                </Text>
                <Text>{data.summary}</Text>
              </Flex>
            ) : null}

            {data?.actionItems?.length ? (
              <Flex vertical gap={8}>
                <Text className="h5 font-weight-500">
                  ● {t("recap.actionItems")}
                </Text>

                <Table
                  className="recap-print-table"
                  columns={actionItemColumns}
                  dataSource={data.actionItems}
                  rowKey="id"
                  pagination={false}
                />
              </Flex>
            ) : null}

            {data?.decisions?.length ? (
              <Flex vertical gap={8}>
                <Text className="h5 font-weight-500">
                  ● {t("recap.importantDecisions")}
                </Text>

                <Table
                  className="recap-print-table"
                  columns={decisionColumns}
                  dataSource={data.decisions}
                  rowKey="id"
                  pagination={false}
                />
              </Flex>
            ) : null}

            <Flex justify="center" className="recap-print__footer">
              <Text>
                {t("recap.footerPrefix")}{" "}
                <Text className="recap-print__footer-brand">
                  {t("recap.footerBrand")}
                </Text>{" "}
                {t("recap.footerSuffix")}
              </Text>
            </Flex>
          </Flex>
        </div>
      </div>
    </>
  );
};

export default RecapPrint;
